import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Space,
  Table,
  Tag,
  message,
  Typography,
  Descriptions,
  Alert,
} from 'antd';
import { SearchOutlined, SyncOutlined, ReloadOutlined } from '@ant-design/icons';
import api, { getApiErrorMessage } from '../utils/api';

const { Title, Text } = Typography;

const statusColor = (status) => {
  const value = String(status || '').toUpperCase();
  if (value === 'ACTIVE') return 'green';
  if (value === 'PAUSED') return 'default';
  if (value === 'WITH_ISSUES' || value === 'DISAPPROVED') return 'red';
  return 'orange';
};

const formatIssues = (issues) => {
  if (!issues || !issues.length) return '-';
  return issues
    .map((item) => {
      const code = item.error_code ? `#${item.error_code}` : '';
      const summary = item.error_summary || item.error_message || '未知错误';
      return `${code} ${summary}`.trim();
    })
    .join('；');
};

const CampaignLookup = () => {
  const [campaignId, setCampaignId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selectedAdIds, setSelectedAdIds] = useState([]);
  const [refreshResults, setRefreshResults] = useState(null);

  const ads = detail?.ads || [];
  const issueAds = useMemo(
    () => ads.filter((row) => row.has_issue && row.ad_id),
    [ads]
  );

  const handleSearch = async (options = {}) => {
    const { keepRefreshResults = false } = options;
    const cid = String(campaignId || '').trim();
    if (!cid) {
      message.warning('请输入系列 ID');
      return;
    }

    setLoading(true);
    if (!keepRefreshResults) {
      setRefreshResults(null);
    }
    try {
      const params = {};
      if (String(accountId || '').trim()) {
        params.account_id = String(accountId).trim();
      }
      const response = await api.get(`/campaign-ops/${cid}`, { params });
      const data = response.data || {};
      setDetail(data);
      const defaultSelected = (data.ads || [])
        .filter((row) => row.has_issue && row.ad_id)
        .map((row) => row.ad_id);
      setSelectedAdIds(defaultSelected);
      message.success('查询成功');
    } catch (error) {
      setDetail(null);
      setSelectedAdIds([]);
      message.error(getApiErrorMessage(error, '查询失败'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!detail?.campaign?.campaign_id) {
      message.error('请先搜索系列');
      return;
    }
    if (!selectedAdIds.length) {
      message.warning('请先选择要刷新的广告');
      return;
    }

    setRefreshLoading(true);
    try {
      const response = await api.post('/campaign-ops/ads/refresh-status', {
        campaign_id: String(detail.campaign.campaign_id),
        ad_ids: selectedAdIds,
        account_id: detail.campaign.account_id || undefined,
      });
      const data = response.data || {};
      setRefreshResults(data);
      const { success_count: ok = 0, failed_count: fail = 0, batch_calls: batches = 1 } = data;
      if (fail === 0) {
        message.success(`Batch 已提交 ${ok} 条（${batches} 次 Batch 调用）`);
      } else {
        message.warning(`Batch 成功 ${ok} 条，失败 ${fail} 条（${batches} 次调用）`);
      }
      await handleSearch({ keepRefreshResults: true });
    } catch (error) {
      message.error(getApiErrorMessage(error, '刷新广告状态失败'));
    } finally {
      setRefreshLoading(false);
    }
  };

  const columns = [
    {
      title: '广告组',
      dataIndex: 'adset_name',
      width: 180,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div>{text || '-'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.adset_id || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: '广告',
      dataIndex: 'ad_name',
      width: 200,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div>{text || '-'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.ad_id || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => <Tag color={statusColor(value)}>{value || '-'}</Tag>,
    },
    {
      title: '有效状态',
      dataIndex: 'effective_status',
      width: 120,
      render: (value) => <Tag color={statusColor(value)}>{value || '-'}</Tag>,
    },
    {
      title: '报错信息',
      dataIndex: 'issues',
      ellipsis: true,
      render: (issues, record) => (
        record.has_issue ? (
          <Text type="danger">{formatIssues(issues)}</Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedAdIds,
    onChange: (keys) => setSelectedAdIds(keys),
    getCheckboxProps: (record) => ({
      disabled: !record.ad_id,
    }),
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={2} style={{ margin: 0 }}>系列查询</Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Facebook 系列 ID"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            style={{ width: 260 }}
            onPressEnter={handleSearch}
          />
          <Input
            placeholder="账户 ID（可选）"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            style={{ width: 200 }}
            onPressEnter={handleSearch}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            onClick={handleSearch}
          >
            搜索
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            数据来自 Facebook API。刷新状态使用 Batch 批量 POST 更新（单次最多 50 条），请更新后重新搜索确认报错是否消失。
          </Text>
        </div>
      </Card>

      {detail?.campaign && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions column={3} size="small">
            <Descriptions.Item label="系列名称">
              {detail.campaign.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="系列 ID">
              {detail.campaign.campaign_id}
            </Descriptions.Item>
            <Descriptions.Item label="账户 ID">
              {detail.campaign.account_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusColor(detail.campaign.status)}>
                {detail.campaign.status || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="有效状态">
              <Tag color={statusColor(detail.campaign.effective_status)}>
                {detail.campaign.effective_status || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="报错广告">
              {detail.counts?.issue_count ?? 0} / {detail.counts?.ad_count ?? 0}
            </Descriptions.Item>
          </Descriptions>

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={refreshLoading}
              disabled={!selectedAdIds.length}
              onClick={handleRefreshStatus}
            >
              重新更新广告状态
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={() => {
                setSelectedAdIds(issueAds.map((row) => row.ad_id));
              }}
              disabled={!issueAds.length}
            >
              全选报错广告 ({issueAds.length})
            </Button>
            <Text type="secondary">
              Batch 直接 POST 更新广告 status（不额外 GET/切换 PAUSED）
            </Text>
          </Space>
        </Card>
      )}

      {refreshResults?.results?.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type={refreshResults.failed_count ? 'warning' : 'success'}
          showIcon
          message={`刷新完成：成功 ${refreshResults.success_count} 条，失败 ${refreshResults.failed_count} 条`}
          description={(
            <div>
              {refreshResults.results.map((item) => (
                <div key={item.ad_id}>
                  {item.ad_name || item.ad_id}：
                  {item.message}
                </div>
              ))}
            </div>
          )}
        />
      )}

      <Card title="广告列表">
        <Table
          rowKey={(record) => record.ad_id || `${record.adset_id}-empty`}
          loading={loading}
          columns={columns}
          dataSource={ads}
          rowSelection={rowSelection}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: '请先搜索系列' }}
        />
      </Card>
    </div>
  );
};

export default CampaignLookup;
